import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SectionBuilder from '../PageBuilder/SectionBuilder/SectionBuilder';
import { FireWeatherBanner } from '../../components/FireWeatherBanner';

import { ASSET_NAME } from './LandingPage.duck';
import css from './LandingPage.module.css';

import yubaBridge from '../../assets/yuba-bridge.webp';

// Hero background videos
import heroDesktopVideo from '../../assets/hero-desktop.mp4';
import heroMobileVideo from '../../assets/hero-mobile.mp4';



const TRUST_INFO = [
  {
    heading: 'Easy & Free',
    items: [
      { icon: '🔒', text: 'Your info is secure and never shared without permission' },
      { icon: '💰', text: '100% free — no hidden fees or obligations' },
      { icon: '⏱️', text: 'Takes less than 3 minutes to complete' },
    ],
    cta: 'Submit a Request',
    ctaRoute: 'RequestIntakeBasePage',
  },
  {
    heading: 'Local Professionals',
    items: [
      { icon: '🔍', text: 'Defensible space, tree work, and home hardening professionals' },
      { icon: '📍', text: 'See professionals who list your county in their service area' },
      { icon: '📋', text: 'Look up any contractor’s CSLB license at cslb.ca.gov before hiring' },
    ],
    cta: 'Find Local Professionals',
    ctaRoute: 'RequestIntakeBasePage',
  },
  {
    heading: 'You Decide',
    items: [
      { icon: '📋', text: 'Compare quotes side by side — zero pressure' },
      { icon: '🤝', text: 'Choose the provider that fits your budget and timeline' },
      { icon: '🛡️', text: 'We support you throughout the entire process' },
    ],
    cta: 'Get Started Free',
    ctaRoute: 'RequestIntakeBasePage',
  },
];

export const LandingPageComponent = props => {
  const { scrollingDisabled, pageAssetsData, inProgress } = props;
  const sectionRef = useRef(null);

  // Trust overlay injection on Our Process cards (section-1)
  useEffect(() => {
    if (!sectionRef.current) return;
    const section = sectionRef.current.querySelector('#section-1');
    if (!section) return;

    const cards = section.querySelectorAll('[class*="BlockContainer_root"]');
    if (cards.length === 0) return;

    const cleanups = [];

    cards.forEach((card, i) => {
      if (i >= TRUST_INFO.length) return;

      card.style.cursor = 'pointer';
      card.style.position = 'relative';

      const info = TRUST_INFO[i];
      const overlay = document.createElement('div');
      overlay.className = css.trustOverlay;
      const itemsHtml = info.items.map(item =>
        `<li class="${css.trustItem}"><span class="${css.trustItemIcon}">${item.icon}</span><span class="${css.trustItemText}">${item.text}</span></li>`
      ).join('');
      overlay.innerHTML = `
        <div class="${css.trustContent}">
          <h4 class="${css.trustHeading}">${info.heading}</h4>
          <ul class="${css.trustList}">${itemsHtml}</ul>
          <a href="/request" class="${css.trustCta}">${info.cta}</a>
          <span class="${css.trustHint}">Click anywhere to close</span>
        </div>
      `;
      overlay.style.display = 'none';
      card.appendChild(overlay);

      const handler = (e) => {
        if (e.target.closest('a')) return;
        e.stopPropagation();
        e.preventDefault();
        const isVisible = overlay.style.display !== 'none';
        overlay.style.display = isVisible ? 'none' : 'flex';
      };
      card.addEventListener('click', handler, true);
      cleanups.push(() => {
        card.removeEventListener('click', handler);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      });
    });

    return () => cleanups.forEach(fn => fn());
  }, [pageAssetsData]);

  // Inject selling points into the Join section (section-4)
  useEffect(() => {
    if (!sectionRef.current) return;
    const section = sectionRef.current.querySelector('#section-4');
    if (!section) return;

    const content = section.querySelector('[class*="sectionContent"]');
    if (!content) return;

    if (content.querySelector(`.${css.joinSellingPoints}`)) return;

    const sellingPoints = document.createElement('div');
    sellingPoints.className = css.joinSellingPoints;
    sellingPoints.innerHTML = `
      <p class="${css.joinCallIn}">Looking for customers?</p>
      <ul class="${css.joinList}">
        <li class="${css.joinListItem}">Reach homeowners actively searching for wildfire mitigation</li>
        <li class="${css.joinListItem}">Get matched with high-intent requests in your service area</li>
        <li class="${css.joinListItem}">Build your professional profile and showcase past work</li>
        <li class="${css.joinListItem}">Free to join — no upfront costs</li>
        <li class="${css.joinListItem}">Join a growing network of California fire defense professionals</li>
      </ul>
    `;

    const header = content.querySelector('header');
    if (header) {
      header.parentNode.insertBefore(sellingPoints, header);
    }

    return () => {
      if (sellingPoints.parentNode) sellingPoints.parentNode.removeChild(sellingPoints);
    };
  }, [pageAssetsData]);

  // Inject geometric divider between Join (section-4) and Our Mission (section-5)
  useEffect(() => {
    if (!sectionRef.current) return;
    const section4 = sectionRef.current.querySelector('#section-4');
    const section5 = sectionRef.current.querySelector('#section-5');
    if (!section4 || !section5) return;

    if (section4.nextElementSibling?.classList?.contains(css.sectionDivider)) return;

    const divider = document.createElement('div');
    divider.className = css.sectionDivider;
    divider.innerHTML = `
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none" class="${css.dividerSvg}">
        <path
          d="M0,120 L0,70 L180,50 L360,75 L540,35 L720,60 L900,40 L1100,70 L1280,45 L1440,55 L1440,120 Z"
          class="${css.dividerBack}"
        />
        <path
          d="M0,120 L0,85 L200,70 L400,88 L600,62 L800,80 L1000,68 L1200,82 L1440,72 L1440,120 Z"
          class="${css.dividerMid}"
        />
        <path
          d="M0,120 L0,98 L240,92 L480,100 L720,88 L960,96 L1200,90 L1440,94 L1440,120 Z"
          class="${css.dividerFront}"
        />
      </svg>
    `;

    section4.parentNode.insertBefore(divider, section5);

    return () => {
      if (divider.parentNode) divider.parentNode.removeChild(divider);
    };
  }, [pageAssetsData]);

  // Override section-2 subtitle on mobile: "hover over" → "tap on"
  useEffect(() => {
    if (!sectionRef.current) return;
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const section2 = sectionRef.current.querySelector('#section-2');
    if (!section2) return;

    const description = section2.querySelector('[class*="SectionContainer_description"], [class*="description"] p, [class*="sectionDescription"]');
    if (description) {
      description.textContent = 'Tap on a category to learn more';
      return;
    }
    const paragraphs = section2.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (/hover over a category/i.test(p.textContent)) {
        p.textContent = 'Tap on a category to learn more';
      }
    });
  }, [pageAssetsData]);

  // Inject Yuba bridge image next to Our Mission header (section-5)
  useEffect(() => {
    if (!sectionRef.current) return;
    const section5 = sectionRef.current.querySelector('#section-5');
    if (!section5) return;

    const content = section5.querySelector('[class*="sectionContent"]');
    if (!content) return;

    if (content.querySelector(`.${css.missionImage}`)) return;

    const header = content.querySelector('header');
    if (!header) return;

    const wrapper = document.createElement('div');
    wrapper.className = css.missionHeaderRow;

    header.parentNode.insertBefore(wrapper, header);
    wrapper.appendChild(header);

    const imgWrapper = document.createElement('div');
    imgWrapper.className = css.missionImage;
    imgWrapper.innerHTML = `<img src="${yubaBridge}" alt="South Yuba River Bridge" loading="lazy" />`;
    wrapper.appendChild(imgWrapper);

    return () => {
      if (wrapper.parentNode) {
        wrapper.parentNode.insertBefore(header, wrapper);
        wrapper.parentNode.removeChild(wrapper);
      }
    };
  }, [pageAssetsData]);

  // Get CMS sections from Sharetribe, skip the hero (we render our own)
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data || {};
  const cmsSections = (pageData.sections || [])
    .filter(s => s.sectionType !== 'hero')
    .map((s, i) => {
      if (i === 0) {
        return {
          ...s,
          description: {
            fieldType: 'paragraph',
            content: 'Submit a wildfire mitigation request, our platform connects you with local professionals that match your needs, they send you quotes, and you choose a contractor to get started on your project.',
          },
        };
      }
      return s;
    });


  return (
    <Page
      title="CA Fire Defense — Turn Risk into Readiness | Wildfire Mitigation Contractors, Nevada County"
      description="CA Fire Defense connects homeowners with local wildfire mitigation contractors — defensible space, tree work, home hardening. Turn risk into readiness. Free quotes, no obligation."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.root} ref={sectionRef}>
          {/* Fire Weather Alert Banner */}
          <FireWeatherBanner />

          {/* Hero */}
          <div className={css.heroWrapper}>
            {/* Video area — 3/4 screen */}
            <div className={css.heroVideoArea}>
              <video
                className={css.heroVideoDesktop}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
              >
                <source src={heroDesktopVideo} type="video/mp4" />
              </video>
              <video
                className={css.heroVideoMobile}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
              >
                <source src={heroMobileVideo} type="video/mp4" />
              </video>
              <div className={css.heroOverlay} aria-hidden="true" />

              {/* Card — overlaps video on desktop */}
              <div className={css.heroCardColumn}>
                <div className={css.heroCardDesktop}>
                  {/* Roofline over the card — decorative, sits in the space above it */}
                  <svg
                    className={css.heroRoofline}
                    viewBox="0 0 300 68"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M0 68 L150 0 L210 27.2 L210 6.8 L234 6.8 L234 38.1 L300 68"
                      stroke="#d4a91e"
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <h1 className={css.heroTitle}>
                    <span className={css.heroTitleLine}>Defensible Space,</span>
                    <span className={css.heroTitleAccent}>A Click Away.</span>
                  </h1>
                  <NamedLink name="RequestIntakeBasePage" className={css.heroCtaPrimary}>
                    Start Your Request
                  </NamedLink>
                </div>
                <div className={css.heroTagline}>
                  <p className={css.heroTaglineLine1}>
                    California's Wildfire Mitigation Marketplace
                  </p>
                </div>
                <div className={css.bannerColumn}>
                  <a href="/resources" className={css.fairPlanBanner}>
                    <span className={css.fairPlanBadge}>Get a letter?</span>
                    <span className={css.fairPlanLink}>
                      FAIR Plan policyholders — see how to save up to 16.4% on your premium →
                    </span>
                  </a>
                  <a href="/signup" className={css.vendorBanner}>
                    <span className={css.vendorBadge}>Contractors</span>
                    <span className={css.vendorLink}>
                      Join our network — get matched with local homeowners ready to hire →
                    </span>
                  </a>
                </div>
              </div>
            </div>

            {/* Card — below video on mobile */}
            <div className={css.heroCardMobile}>
              {/* Roof over the card — with the card's side/bottom rules it reads as a house */}
              <div className={css.heroRoofMobile} aria-hidden="true">
                <svg viewBox="0 0 402 70" preserveAspectRatio="none" focusable="false">
                  <polygon
                    points="0,70 201,0 281.4,28 281.4,5.6 309.5,5.6 309.5,37.8 402,70"
                    fill="rgba(255, 255, 255, 0.85)"
                  />
                  <path
                    d="M18 70 L0 70 L201 0 L281.4 28 L281.4 5.6 L309.5 5.6 L309.5 37.8 L402 70 L384 70"
                    fill="none"
                    stroke="#1a1e18"
                    strokeWidth="3"
                    strokeLinejoin="miter"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
              <h1 className={css.heroTitle}>
                <span className={css.heroTitleLine}>Defensible Space,</span>
                <span className={css.heroTitleAccent}>A Click Away.</span>
              </h1>
              <NamedLink name="RequestIntakeBasePage" className={css.heroCtaPrimary}>
                Start Your Request
              </NamedLink>
            </div>
            <div className={css.heroTaglineMobile}>
              <p className={css.heroTaglineLine1}>
                California's Wildfire Mitigation Marketplace
              </p>
            </div>

            {/* Tagline statement — below the video area */}
            <div className={css.heroStatement}>
              <p className={css.heroStatementText}>Turn risk into readiness.</p>
              <div className={css.toolRow} aria-hidden="true">
                {/* Weed whacker */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 4l13 15" />
                  <path d="M8 4L5 6.5" />
                  <circle cx="13.5" cy="8.5" r="2.4" />
                  <circle cx="23" cy="21.5" r="2.2" />
                  <path d="M18.5 25.5a6.5 6.5 0 0 0 9.5-2" />
                  <path d="M25 29v-2.5M28.5 28v-3.5M21.5 29.5v-2" />
                </svg>
                {/* Chainsaw */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="12" width="9" height="9" rx="2" />
                  <path d="M6.5 12V9.5a3 3 0 0 1 3-3H12" />
                  <path d="M13 15.5h13a3.2 3.2 0 0 1 0 6.4H13" />
                </svg>
                {/* Hard hat */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 19a9 9 0 0 1 18 0" />
                  <path d="M3.5 19h25" />
                  <path d="M16 10v4" />
                </svg>
                {/* Hammer */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9.5" y="4.5" width="13" height="6" rx="1" transform="rotate(20 16 7.5)" />
                  <path d="M13.5 12L6 27" />
                </svg>
                {/* Shovel */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 4h8" />
                  <path d="M16 4v13" />
                  <path d="M16 17c-3 .5-5 2.5-5 5.5 0 2 .7 3.8 2 5.5h6c1.3-1.7 2-3.5 2-5.5 0-3-2-5-5-5.5z" />
                </svg>
                {/* Chipper */}
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 15h12v9H10z" />
                  <circle cx="13" cy="26.5" r="2" />
                  <circle cx="19" cy="26.5" r="2" />
                  <path d="M10 16L3.5 9.5M14 14.5L7 7.5M3.5 9.5L7 7.5" />
                  <path d="M20 15V9c0-2 1.6-3.2 3.5-3.2" />
                  <path d="M26.5 5.5l2-1M27 8.5l2.2-.4" />
                </svg>
              </div>
            </div>

            {/* Banner row — below video on mobile (desktop shows these in the card column) */}
            <div className={css.bannerRow}>
              <a href="/resources" className={css.fairPlanBanner}>
                <span className={css.fairPlanBadge}>Get a letter?</span>
                <span className={css.fairPlanLink}>
                  FAIR Plan policyholders — see how to save up to 16.4% on your premium →
                </span>
              </a>
              <a href="/signup" className={css.vendorBanner}>
                <span className={css.vendorBadge}>Contractors</span>
                <span className={css.vendorLink}>
                  Join our network — get matched with local homeowners ready to hire →
                </span>
              </a>
            </div>

            {/* Trust text */}
            <p className={css.trustInCard}>
              Founded in Nevada County · Built for Wildfire Country · No Obligation · Free for Homeowners
            </p>
          </div>

          {/* CMS-driven sections from Sharetribe (Our Process, etc.) */}
          {cmsSections.length > 0 && (
            <SectionBuilder sections={cmsSections} options={{}} />
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress } = state.hostedAssets || {};
  return {
    scrollingDisabled: isScrollingDisabled(state),
    pageAssetsData,
    inProgress,
  };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
